/**
 * Every react-query key the app's data hooks use, in one place. The arrays are the cache's identity:
 * invalidating a prefix (e.g. queryKeys.assets()) refreshes every key that starts with it, and
 * api/events.ts patches some of them in place, so a key's shape never changes here.
 */
export const queryKeys = {
  me: () => ['me'] as const,
  /** Every Library list (prefix of library()). */
  assets: () => ['assets'] as const,
  library: (params: string) => ['assets', params] as const,
  /** A facet search box's suggestions (not under assets(): those keys hold Library pages). */
  facetValues: (params: string) => ['facet-values', params] as const,
  asset: (id: string | null | undefined) => ['asset', id] as const,
  /** Without a kind: every filter of the asset's log (for invalidation). */
  assetAudit: (id: string, kind?: string) => (kind ? ['asset-audit', id, kind] : ['asset-audit', id]) as readonly string[],
  attachments: (assetId: string | undefined) => ['attachments', assetId] as const,
  schema: () => ['schema'] as const,
  adminModel: () => ['admin-model'] as const,
  storage: () => ['storage'] as const,
  agents: () => ['agents'] as const,
  transfers: (assetId: string) => ['transfers', assetId] as const,
  exportDestinations: () => ['export-destinations'] as const,
  /** Send to edit (1.0.0-rc.6): edit locations, and hand-offs (all, or a sequence's). */
  editDestinations: () => ['edit-destinations'] as const,
  handoffs: (sequenceId?: string) => (sequenceId ? ['handoffs', sequenceId] : ['handoffs']) as readonly string[],
  strata: () => ['strata'] as const,
  /** 1.8.1: the layers with their segment counts (Administration → Layers); under strata(), so it refreshes with it. */
  strataCounts: () => ['strata', 'counts'] as const,
  segments: (assetId: string | undefined) => ['segments', assetId] as const,
  watchFolders: () => ['watch-folders'] as const,
  watchFolderItems: (id: string | null) => ['watch-folder-items', id] as const,
  /** Every recycle bin search (prefix of recycleBin()). */
  recycleBins: () => ['recycle-bin'] as const,
  recycleBin: (q: string) => ['recycle-bin', q] as const,
  recycleSettings: () => ['recycle-settings'] as const,
  savedSearches: () => ['saved-searches'] as const,
  people: (q: string) => ['people', q] as const,
  sequences: () => ['sequences'] as const,
  sequence: (id: string | undefined) => ['sequence', id] as const,
  sequenceSources: (id: string | undefined, revision: number | undefined) => ['sequence-sources', id, revision] as const,
  templates: () => ['templates'] as const,
  /** The Graphics area (0.92.0): every list and graphic share the prefix, so a save refreshes them all. */
  graphicsAll: () => ['graphics'] as const,
  graphics: (params: string) => ['graphics', 'list', params] as const,
  graphic: (id: string | null | undefined) => ['graphics', 'one', id] as const,
  graphicsSettings: () => ['graphics-settings'] as const,
  templateBundle: (id: string | undefined, version: number | undefined) => ['template-bundle', id, version] as const,
  exports: (sequenceId: string | undefined) => ['exports', sequenceId] as const,
  /** An asset's subtitle tracks; its transcriptions share the prefix so one event refreshes both. */
  subtitles: (assetId: string | undefined) => ['subtitles', assetId] as const,
  transcriptions: (assetId: string | undefined) => ['subtitles', assetId, 'transcriptions'] as const,
  runnableWorkflows: (subject: 'asset' | 'sequence') => ['runnable-workflows', subject] as const,
  automations: () => ['automations'] as const,
  automation: (id: string | undefined) => ['automation', id] as const,
  automationRuns: (params: string) => ['automation-runs', params] as const,
  automationRun: (id: string | undefined) => ['automation-run', id] as const,
  /** Every approval list and single approval (prefix of the two below). */
  approvalsAll: () => ['approvals'] as const,
  approvals: (params: string) => ['approvals', params] as const,
  approval: (id: string | null) => ['approvals', 'one', id] as const,
  approvalNotes: (id: string | null) => ['approval-notes', id] as const,
  shares: (assetId: string) => ['shares', assetId] as const,
  /** 1.6.3 Administration → Shared links: every list (prefix of the one below) and a link's view history. */
  sharesAdminAll: () => ['shares-admin'] as const,
  sharesAdmin: (params: string) => ['shares-admin', params] as const,
  shareViews: (shareId: string) => ['shares-admin', 'views', shareId] as const,
  webhookEndpoints: () => ['webhook-endpoints'] as const,
  operations: () => ['operations'] as const,
  roles: () => ['roles'] as const,
  members: () => ['members'] as const,
  accessGroups: () => ['access-groups'] as const,
  transcoders: () => ['transcoders'] as const,
  collections: () => ['collections'] as const,
  collectionItems: (id: string | null) => ['collection-items', id] as const,
  collectionsContaining: (subject: 'asset' | 'sequence', id: string) => ['collections-containing', subject, id] as const,
  transcodeProfiles: () => ['transcode-profiles'] as const,
  /** Automated QC (1.0.0-rc.8): an asset's runs and one run share the qc prefix, so one event refreshes both. */
  qcProfiles: () => ['qc-profiles'] as const,
  qcSettings: () => ['qc-settings'] as const,
  assetQc: (assetId: string | undefined) => ['qc', 'asset', assetId] as const,
  qcRun: (runId: string | null | undefined) => ['qc', 'run', runId] as const,
  categories: () => ['categories'] as const,
  relations: (assetId: string) => ['relations', assetId] as const,
  /** Every task list and the summary (prefix of the two below). */
  tasksAll: () => ['tasks'] as const,
  tasks: (params: string) => ['tasks', params] as const,
  taskSummary: () => ['tasks', 'summary'] as const,
  taskAssignees: () => ['task-assignees'] as const,
  /** The Admin rail badge (1.2.8; GET /api/v1/system/alerts/summary), set from System status's own query too. */
  alertSummary: () => ['system-alerts', 'summary'] as const,
  taskNotes: (id: string | null) => ['task-notes', id] as const,
  /** Storage migrations (1.1.0): every list, migration and item page share the prefix, so one refresh covers them. */
  migrations: () => ['storage-migrations'] as const,
  migration: (id: string | null | undefined) => ['storage-migrations', 'one', id] as const,
  migrationItems: (id: string | null | undefined, status: string) => ['storage-migrations', 'items', id, status] as const,
  /** Transfer windows (1.1.0), shared by migrations and tiering rules. */
  transferWindows: () => ['transfer-windows'] as const,
  /** Master variants (1.2.0): the workspace's list (Administration → Metadata model → Master variants). */
  masterVariants: () => ['master-variants'] as const,
  /** An asset's masters (1.2.0; GET /api/v1/assets/:id/masters). The asset's own key (asset()) carries them too. */
  assetMasters: (assetId: string | null | undefined) => ['masters', assetId] as const,
  /** The batch dialogs' per-asset preflight (1.2.0): kind, the selection's ids and the dialog's choices. */
  batchPreflight: (params: string) => ['batch-preflight', params] as const,
  /** An asset's versions (1.2.3; GET /api/v1/assets/:id/versions, the Versions tab). */
  assetVersions: (assetId: string | null | undefined) => ['versions', assetId] as const,
  /** Old versions cleanup jobs (1.2.3; Administration → Storage → Old versions). */
  oldVersionJobs: () => ['old-version-jobs'] as const,

  /* ---------------------------------------------------------------- 1.3.0 media portal (step 0 defines every key) */
  /** Administration → Media portal (P1, api/hooks/portalAdmin.ts): every key below starts with portalAdmin(). */
  portalAdmin: () => ['portal-admin'] as const,
  portalSettings: () => ['portal-admin', 'settings'] as const,
  portalCompanies: () => ['portal-admin', 'companies'] as const,
  portalCompany: (id: string | null | undefined) => ['portal-admin', 'companies', id] as const,
  portalUsers: (companyId: string | null | undefined) => ['portal-admin', 'users', companyId] as const,
  portalCompanyUploads: (companyId: string | null | undefined) => ['portal-admin', 'uploads', companyId] as const,
  deliverySpecs: () => ['portal-admin', 'specs'] as const,
  deliverySpec: (id: string | null | undefined) => ['portal-admin', 'specs', id] as const,
  uploadRules: () => ['portal-admin', 'rules'] as const,
  /** Assignments (P2, api/hooks/assignments.ts): every key below starts with assignments(). */
  assignments: () => ['assignments'] as const,
  assignmentList: (params: string) => ['assignments', 'list', params] as const,
  assetAssignment: (assetId: string | null | undefined) => ['assignments', 'asset', assetId] as const,
  assignment: (id: string | null | undefined) => ['assignments', 'one', id] as const,
  /** Intake (P4, api/hooks/intake.ts): every key below starts with intake(). */
  intake: () => ['intake'] as const,
  intakeSummary: () => ['intake', 'summary'] as const,
  intakeList: (params: string) => ['intake', 'list', params] as const,
  intakeSubmission: (id: string | null | undefined) => ['intake', 'one', id] as const,
  intakeNotes: (id: string | null | undefined) => ['intake', 'notes', id] as const,

  /* ---------------------------------------------------------------- 1.4.0 asset groups (step 0 defines every key) */
  /** Group types (api/hooks/groups.ts): Administration → Metadata model → Group types, and every picker. */
  groupTypes: () => ['group-types'] as const,
  /** Groups (api/hooks/groups.ts): every key below starts with groups(), so one change refreshes them all. */
  groups: () => ['groups'] as const,
  groupList: (params: string) => ['groups', 'list', params] as const,
  group: (id: string | null | undefined) => ['groups', 'one', id] as const,
  groupMembers: (id: string | null | undefined) => ['groups', 'members', id] as const,
  /** Group search suggestions in the Library's search (G1). */
  groupSuggestions: (q: string) => ['groups', 'suggest', q] as const,
  /** A group assignment's preview (G2, api/hooks/groups-admin.ts; under assignments() so an assignment refreshes it). */
  groupAssignmentPreview: (params: string) => ['assignments', 'group-preview', params] as const,
  /**
   * The Grouped view (G3, api/hooks/grouped.ts): one level (root or a group) for the Library's filters. Not under
   * assets(): api/events.ts patches those as Library pages; it invalidates this prefix when assets change.
   */
  groupedAll: () => ['assets-grouped'] as const,
  grouped: (params: string, parent: string) => ['assets-grouped', params, parent] as const,
  /* ---------------------------------------------------------------- 1.5.0 shot detection (step 0 defines every key) */
  /** The workspace's shot detection settings (api/hooks/shots.ts: the Transcoding card and the Detect shots dialog). */
  shotSettings: () => ['shot-settings'] as const,
  /** The asset's shots come from its segments (queryKeys.segments, the Shots layer s_shots): no key of their own. */
  /* ---------------------------------------------------------------- 1.6.0 (step 0 defines every key; docs/dev/mcp-1.6.0-build.md) */
  /** T: My account → AI assistant access, and the person's own tokens. */
  myAssistantAccess: () => ['my-assistant-access'] as const,
  myTokens: () => ['my-tokens'] as const,
  /** T: Access → AI assistants (the settings, every person's tokens) and the Integrations page's downloads. */
  assistantSettings: () => ['assistant-settings'] as const,
  adminTokens: (state: string) => ['admin-tokens', state] as const,
  mcpDownloads: () => ['mcp-downloads'] as const,
  /** W: the agents' site services (the Webhooks page section and the builder's pickers). */
  siteServices: () => ['site-services'] as const,
  /** A: an asset's metadata suggestions (its segment suggestions are in queryKeys.segments). */
  metadataSuggestions: (assetId: string | undefined) => ['metadata-suggestions', assetId] as const,
  /* ---------------------------------------------------------------- 1.8.0 rights (step 0 defines every key; docs/dev/rights-1.8.0-build.md) */
  /** The workspace's rights settings (api/hooks/rights.ts: Administration → Rights, every dialog that checks rights). */
  rightsSettings: () => ['rights-settings'] as const,
  /** An asset's genealogy, a page of its Used in per cursor; genealogyAll(assetId) refreshes them all. */
  genealogyAll: (assetId: string | undefined) => ['genealogy', assetId] as const,
  genealogy: (assetId: string | undefined, cursor: string | null) => ['genealogy', assetId, cursor] as const,
  /** Administration → Rights: the counts per state (Withdrawn, Ended, Ends within N days…). */
  rightsCounts: () => ['rights-counts'] as const,
};
